// src/pages/administrativo/FilialCad.js
import React, { useState, useEffect } from 'react';

import Input from '../../components/input/Input.js';
import { useFilial } from '../../contexts/administrativo/FilialContext.js'; // Aqui você deve ter um contexto de Filial similar ao de Empresa
import FilialSearch from './FilialSearch';
import { Filial } from '../../models/geral/Filial.ts';
import EmpresaSearch from './EmpresaSearch.js';
import InputCheck from '../../components/input/InputCheck.js';
import { useEmpresa } from '../../contexts/administrativo/EmpresaContext.js';

import EnderecoForm from '../../components/form/pessoa/EnderecoForm.js';
import { useMessage } from '../../contexts/geral/MessageContext.js';
import { useYesNo } from '../../contexts/geral/YesNoContext.js';
import {
    createBase64ImageSource,
    getImageMimeType,
    readImageFile,
    validateDeliveryLogoFile,
} from '../../utils/ImageUtils.js';
import ModalContainer from '../../components/modalForm/ModalContainer';
import ModalHeader from '../../components/modalForm/ModalHeader';
import ModalBody from '../../components/modalForm/ModalBody';
import ModalFooter from '../../components/modalForm/ModalFooter';
import ModalTabs from '../../components/modalForm/ModalTabs';
import ModalActions, { ModalActionButton } from '../../components/modalForm/ModalActions';

const FilialCad = ({ show, onHide }) => {
    const {
        filiais,
        addFilial,
        updateFilial,
        deleteFilial,
        fetchFilialLogo,
        updateFilialLogo,
        fetchFilialLogoDelivery,
        updateFilialLogoDelivery,
        deleteFilialLogoDelivery,
    } = useFilial();
    const { empresas } = useEmpresa(); // Contexto de empresas
    const [filial, setFilial] = useState(new Filial(0, 0));
    const [logoBase64, setLogoBase64] = useState('');
    const [logoPreview, setLogoPreview] = useState('');
    const [logoNome, setLogoNome] = useState('');
    const [logoDeliveryBase64, setLogoDeliveryBase64] = useState('');
    const [logoDeliveryPreview, setLogoDeliveryPreview] = useState('');
    const [logoDeliveryNome, setLogoDeliveryNome] = useState('');
    const [logoDeliveryAlterada, setLogoDeliveryAlterada] = useState(false);

    const [showSearchModal, setShowSearchModal] = useState(false);
    const [showSearchModalEmpresa, setShowSearchModalEmpresa] = useState(false);
    const {showMessage} = useMessage();
    const { showYesNo } = useYesNo();
    const [novoReg, setNovoReg] = useState(false);

    const [activeTab, setActiveTab] = useState('geral');
    const handleTabChange = (tab) => (e) => {
        e.preventDefault(); // Previne o refresh ao clicar nas tabs
        setActiveTab(tab);
    };

    useEffect(() => {
        setFilial(new Filial(0, 0));
    }, []);

    const limparLogoDelivery = (alterada = false) => {
        setLogoDeliveryBase64('');
        setLogoDeliveryPreview('');
        setLogoDeliveryNome('');
        setLogoDeliveryAlterada(alterada);
    };

    const carregarLogosFilial = async (filialId) => {
        if (!filialId) {
            setLogoBase64('');
            setLogoPreview('');
            setLogoNome('');
            limparLogoDelivery();
            return;
        }

        const [logo, logoDelivery] = await Promise.all([
            fetchFilialLogo(filialId),
            fetchFilialLogoDelivery(filialId),
        ]);

        if (logo) {
            setLogoBase64(logo);
            setLogoPreview(createBase64ImageSource(logo, getImageMimeType(logo)));
            setLogoNome('logo-filial');
        } else {
            setLogoBase64('');
            setLogoPreview('');
            setLogoNome('');
        }

        if (logoDelivery?.imagemBase64) {
            setLogoDeliveryBase64(logoDelivery.imagemBase64);
            setLogoDeliveryPreview(createBase64ImageSource(
                logoDelivery.imagemBase64,
                logoDelivery.mimeType,
            ));
            setLogoDeliveryNome('logo-delivery');
            setLogoDeliveryAlterada(false);
        } else {
            limparLogoDelivery();
        }
    };

    const handleLogoChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            const result = reader.result || '';
            const base64 = typeof result === 'string' ? result.split(',')[1] : '';

            if (!base64) return;
            setLogoBase64(base64);
            setLogoPreview(URL.createObjectURL(file));
            setLogoNome(file.name);
        };
        reader.readAsDataURL(file);
    };

    const handleLogoDeliveryChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const validationMessage = validateDeliveryLogoFile(file);
        if (validationMessage) {
            showMessage(validationMessage);
            e.target.value = '';
            return;
        }

        try {
            const { base64, preview } = await readImageFile(file);
            setLogoDeliveryBase64(base64);
            setLogoDeliveryPreview(preview);
            setLogoDeliveryNome(file.name);
            setLogoDeliveryAlterada(true);
        } catch (error) {
            showMessage(error.message);
        }
    };

    const handleNovo = () => {
        setFilial(new Filial(0, 0));
        setLogoBase64('');
        setLogoPreview('');
        setLogoNome('');
        limparLogoDelivery();
        setNovoReg(true);
    };

    const handleEditar = (id) => {
        if (id === 0) {
            showMessage("Selecione uma filial para editar!");            
            return;
        }
        setNovoReg(false);
    };

    const handleDelete = async () => {
        if (!filial?.id) {
            showMessage("Selecione uma filial para excluir!");        
        } else {
            const resposta = await showYesNo("Deseja excluir a filial selecionada?");
            if (resposta) {
                await deleteFilial(filial.id);
                handleNovo();
            }
        }
    };

    const handleSave = async () => {
        try {
            if (!filial.nome || !filial.cnpj) {
                showMessage("Nome e CNPJ da filial não preenchidos!");                
                return;
            }

            // Criar payload sem os objetos grupo, marca e unMedida
            const payload = {
                ...filial,
                endereco: filial.endereco ? { ...filial.endereco } : filial.endereco
            };

            // Remover propriedades desnecessárias
            delete payload.empresa;
            delete payload.endereco?.cidade;

            let filialSalva;
            if (!filial.id) {
                filialSalva = await addFilial(payload);
            } else {
                filialSalva = await updateFilial(payload);
            }

            const filialId = filialSalva?.id || filial.id;
            if (filialId && logoBase64) {
                await updateFilialLogo(filialId, logoBase64);
            }
            if (filialId && logoDeliveryAlterada) {
                if (logoDeliveryBase64) {
                    await updateFilialLogoDelivery(filialId, logoDeliveryBase64);
                } else {
                    await deleteFilialLogoDelivery(filialId);
                }
            }
            handleNovo();
        } catch (error) {
            console.error("Erro ao gravar dados: ", error);
        }
    };

    const handleExit = () => {
        const filialSelecionada = filiais.find(f => f.id.toString() === filial?.id?.toString());

        if (filialSelecionada) {
            setFilial(filialSelecionada);
            handleEditar(filial?.id);
            carregarLogosFilial(filialSelecionada.id);
        } else {
            setFilial(new Filial(0, 0));
            setLogoBase64('');
            setLogoPreview('');
            setLogoNome('');
            limparLogoDelivery();
        }
    };

    const handleSelectFilial = async (filialSel) => {
        setFilial(filialSel);
        setShowSearchModal(false);
        if (filialSel) {
            handleEditar(filialSel.id);
            await carregarLogosFilial(filialSel.id);
        } else {
            setFilial(new Filial(0, 0));
            setLogoBase64('');
            setLogoPreview('');
            setLogoNome('');
            limparLogoDelivery();
        }
    };

    const handleSelectEmpresa = (empresaSel) => {
        setFilial({...filial,
                empresaId : empresaSel.id,
                empresa: empresaSel});        

        setShowSearchModalEmpresa(false);
    }

    const handleExitEmpresa = (empresaId) => {
        const empresaSelecionada = empresas.find(e => e.id.toString() === empresaId.toString());    
        if (empresaSelecionada) {
            setFilial({...filial,
                empresaId : empresaSelecionada.id,
                empresa: empresaSelecionada});             
        } else {
            setFilial({...filial,
                empresaId : null,
                empresa: null});    
        }            
    }

    if (!show) return null;

    return (
        <ModalContainer show={show} largura="800px" variant="cadastro" onHide={onHide}>

            <ModalHeader title={novoReg ? 'Nova Filial' : 'Alterar Filial'} onHide={onHide} />
            <ModalBody className="modal-body">
                <form>
                    <div className="input-group mb-3">
                        <Input
                            label="Código"
                            value={filial.id || ''}
                            onChange={(e) => setFilial({ ...filial, id: e.target.value })}
                            onBlur={handleExit}
                            disabled={!novoReg}
                            btnBuscar={() => setShowSearchModal(true)}
                        />
                        <Input
                            label="Nome Fantasia"
                            value={filial.nome || ''}
                            onChange={(e) => setFilial({ ...filial, nome: e.target.value })}
                            size={300}
                            autoFocus={true}
                        />
                        <Input
                            label="Nome Razão"
                            value={filial.fantasia || ''}
                            onChange={(e) => setFilial({ ...filial, fantasia: e.target.value })}
                            size={300}
                        />
                    </div>

                    {/* Menu de Tabs */}
                    <ModalTabs  style={{ marginTop: '10px' }}>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'geral' ? 'active' : ''}`}
                                onClick={handleTabChange('geral')}
                            >
                                Geral
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'endereco' ? 'active' : ''}`}
                                onClick={handleTabChange('endereco')}
                            >
                                Endereço
                            </button>
                        </li>
                    </ModalTabs>

                    {/* Scrollable Content */}
                    <ModalTabs.Panel  style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {activeTab === 'geral' && (
                            <div className="section">
                                {/* Adicione inputs específicos para a aba Geral aqui */}
                                <h5>Geral</h5>
                                <div className="input-group mb-3">
                        <Input
                            label="Código"
                            value={filial.empresaId || ''}
                            onChange={(e) => setFilial({ ...filial, empresaId: e.target.value })}
                            onBlur={(e) => handleExitEmpresa(e.target.value)}
                            btnBuscar={() => setShowSearchModalEmpresa(true)}
                        />
                        <Input
                            label="Nome Empresa"
                            value={filial.empresa?.nome || ''}
                            //onChange={(e) => setEmpresa({ ...empresa, nome: e.target.value })}
                            size={300}
                            disabled
                            autoFocus={true}
                        />
                        <InputCheck
                            id="matriz"
                            label="Matriz"
                            checked={filial.matriz}
                            onChange={(e) => setFilial({ ...filial, matriz: e.target.checked })}
                        />
                    </div>

                    <div className="input-group mb-3">
                        <Input
                            label="CNPJ"
                            value={filial.cnpj || ''}
                            onChange={(e) => setFilial({ ...filial, cnpj: e.target.value })}
                            size={170}
                            mask={'99.999.999/9999-99'}
                        />
                        <Input
                            label="IE"
                            value={filial.ie || ''}
                            onChange={(e) => setFilial({ ...filial, ie: e.target.value })}
                            size={150}
                        />

                    </div>
                    <div className="input-group">
                        <Input
                            label="IM"
                            value={filial.im || ''}
                            onChange={(e) => setFilial({ ...filial, im: e.target.value })}
                            size={150}
                        />
                        <Input
                            label="CNAE"
                            value={filial.cnae || ''}
                            onChange={(e) => setFilial({ ...filial, cnae: e.target.value })}
                            size={100}
                        />
                        <Input
                            label="Telefone"
                            value={filial.telefone || ''}
                            onChange={(e) => setFilial({ ...filial, telefone: e.target.value })}
                            size={150}
                            mask={'(99) 9 9999-9999'}
                        />
                        <Input
                            label="Chave Cliente TEF"
                            value={filial.chavePos || ''}
                            onChange={(e) => setFilial({ ...filial, chavePos: e.target.value })}
                            size={150}
                        />
                    </div>

                    <div className="form-group mt-3">
                        <label className="form-label">Logo de impressão</label>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleLogoChange}
                            />
                            <input
                                type="text"
                                className="form-control"
                                readOnly
                                value={logoNome}
                                placeholder="Nenhuma imagem selecionada"
                                style={{ maxWidth: '320px' }}
                            />
                        </div>
                        {logoPreview && (
                            <div className="mt-2">
                                <img
                                    src={logoPreview}
                                    alt="Logo de impressão da filial"
                                    style={{ maxHeight: '120px', border: '1px solid #ddd', padding: '4px' }}
                                />
                            </div>
                        )}
                    </div>

                    <div className="form-group mt-4">
                        <label className="form-label">Logo do cardápio delivery</label>
                        <p className="form-text mb-2">
                            Prefira PNG ou WebP com fundo transparente e sem margens internas. Máximo de 2 MB.
                        </p>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                onChange={handleLogoDeliveryChange}
                            />
                            <input
                                type="text"
                                className="form-control"
                                readOnly
                                value={logoDeliveryNome}
                                placeholder="Nenhuma imagem selecionada"
                                style={{ maxWidth: '320px' }}
                            />
                            {logoDeliveryPreview && (
                                <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => limparLogoDelivery(true)}
                                >
                                    Remover
                                </button>
                            )}
                        </div>
                        {logoDeliveryPreview && (
                            <div className="mt-2">
                                <img
                                    src={logoDeliveryPreview}
                                    alt="Logo do cardápio delivery"
                                    style={{
                                        width: '140px',
                                        height: '100px',
                                        objectFit: 'contain',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        padding: '6px',
                                        background: '#fff',
                                    }}
                                />
                            </div>
                        )}
                    </div>
                        </div>
                        )}
                    </ModalTabs.Panel>

                    {activeTab === 'endereco' && (
                        <div>
                            {/* Adicione inputs específicos para a aba Impostos aqui */}
                            <EnderecoForm
                                endereco={filial.endereco}
                                setEndereco={(newEndereco) =>
                                    setFilial({ ...filial, endereco: newEndereco })
                                }
                            />
                        </div>
                    )}
                </form>
            </ModalBody>
            <ModalFooter className="modal-footer justify-content-end">
                <ModalActions >
                    <ModalActionButton className="btn btn-primary" onClick={handleSave} action="save">
                        Salvar
                    </ModalActionButton>
                    <ModalActionButton className="btn btn-danger" onClick={handleDelete} disabled={novoReg} action="delete">
                        Excluir
                    </ModalActionButton>
                    <ModalActionButton className="btn btn-secondary" onClick={handleNovo} disabled={novoReg} action="new">
                        Novo
                    </ModalActionButton>
                </ModalActions>
            </ModalFooter>

            <FilialSearch
                show={showSearchModal}
                onHide={() => setShowSearchModal(false)}
                onSelectFilial={handleSelectFilial}
            />

            <EmpresaSearch
                show={showSearchModalEmpresa}
                onHide={() => setShowSearchModalEmpresa(false)}
                onSelectEmpresa={handleSelectEmpresa}
            />

        </ModalContainer>
    );
};

export default FilialCad;
